/**
 * The workflow builder's URL: the workflow open in the editor, the step
 * selected in it, and the test open over it — so any of them is a link an
 * author can bookmark or send.
 *
 *   /setup/workflows?id=<workflow>                              the editor
 *   /setup/workflows?id=<workflow>&step=<node>                  a step selected
 *   /setup/workflows?id=<workflow>&test=workflow                a test of the whole workflow
 *   /setup/workflows?id=<workflow>&step=<node>&test=step        a test of the selected step
 */
export interface WorkflowsSearch {
  /** The workflow open in the editor. */
  id?: string;
  /** The node selected in the editor. Only with `id`. */
  step?: string;
  /** The test open over the editor. Only with `id`; `step` only with `step`. */
  test?: 'workflow' | 'step';
}

/** What the editor shows: the selected node, and the test open over it. */
export interface EditorView {
  step: string | null;
  test: 'workflow' | 'step' | null;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/** Drops what does not hang together: anything without a workflow, a step test with no step. */
export function parseWorkflowsSearch(raw: Record<string, unknown>): WorkflowsSearch {
  const id = str(raw.id);
  if (!id) return {};
  return searchFor(id, {
    step: str(raw.step) ?? null,
    test: raw.test === 'workflow' || raw.test === 'step' ? raw.test : null,
  });
}

export function viewOf(search: WorkflowsSearch): EditorView {
  return { step: search.step ?? null, test: search.test ?? null };
}

export function searchFor(id: string, view: EditorView): WorkflowsSearch {
  const test = view.test === 'step' && !view.step ? null : view.test;
  return {
    id,
    ...(view.step ? { step: view.step } : {}),
    ...(test ? { test } : {}),
  };
}
